import classNames from 'classnames';
import { Button } from 'components/atoms/Button';
import { Loader } from 'components/atoms/Loader';
import { Progress } from 'components/atoms/Progress';
import type { FC, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation } from 'react-query';
import { RetryIcon, XIconIcon } from 'utils/icons/figmaIcons';

type AttachmentProps = {
  className?: string;
  icon?: ReactNode;
  label: string;
  onClick?: null | (() => any);
  onRemove?: null | (() => any);
  disabled?: boolean;
  isUploading?: boolean;
  uploadingProgress?: number;
  isError?: boolean;
  retry?: (() => void) | null | false;
};

const Attachment: FC<AttachmentProps> = ({
  className,
  icon,
  label,
  onClick,
  onRemove,
  disabled: _disabled,
  isUploading,
  uploadingProgress,
  isError,
  retry,
}) => {
  const { t } = useTranslation();
  const onClickAsync = useMutation(() => Promise.resolve(onClick?.()));

  const disabled = onClickAsync.isLoading || !onClick || _disabled;

  return (
    <div
      className={classNames(
        className,
        'flex items-center gap-2 bg-primary-25 rounded-lg p-2',
        { 'ring-1 ring-red-500': isError },
      )}
    >
      {icon}
      <Button
        asDefault
        disabled={disabled}
        onClick={() => onClickAsync.mutate()}
        className={classNames(
          { underline: !disabled },
          'text-primary-400 text-sm font-semibold text-init w-1/2 flex items-center gap-2',
        )}
      >
        <span className="truncate-wrap whitespace-nowrap">{label}</span>
        {onClickAsync.isLoading && <Loader className="flex-shrink-0" />}
      </Button>
      <div className="flex-1 flex items-center justify-end gap-4">
        {isUploading && (
          <Progress
            className="flex-1"
            color="blue"
            progress={uploadingProgress || 0}
          />
        )}
        {isError && (
          <span className="text-red-500 text-sm font-semibold">
            {t('backend.error.something_went_wrong')}
          </span>
        )}

        <div className="flex items-center gap-1">
          {isError && retry && (
            <Button
              asDefault
              className="text-primary-400 hover:text-primary-700 focus:text-primary-700"
              onClick={() => retry()}
            >
              <RetryIcon className="w-6 h-6" />
            </Button>
          )}
          {onRemove && (
            <Button
              asDefault
              className="text-primary-400 hover:text-primary-700 focus:text-primary-700"
              onClick={onRemove}
            >
              <XIconIcon className="w-6 h-6" />
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default Attachment;
